import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHero } from "@/components/common/SectionHeading";
import { fetchCampusPublic, submitCampusStory, campusImageUrl } from "@/lib/lnlApi";
import { Camera, Send, X, ImagePlus, Users, Calendar } from "lucide-react";

const CATEGORIES = [
    "Classrooms","Laboratories","Library","Student Activities","Student Organizations",
    "Community Extension","Academic Events","Sports","Guidance Services","Campus Facilities",
];

export default function CampusLife() {
    const [items, setItems] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(true);

    async function load() {
        try {
            setLoading(true);
            const data = await fetchCampusPublic();
            setItems(data || []);
        } catch (e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, []);

    return (
        <div data-testid="campus-life-page">
            <PageHero
                eyebrow="Campus life"
                title="Experience life on campus"
                description="Life at LNL is anchored on academics, values formation, student activities and community extension."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Campus Life" }]}
            />

            {/* Categories — existing admin-curated content grid */}
            <section className="py-10 bg-white">
                <div className="lnl-container">
                    <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                        <div>
                            <div className="lnl-eyebrow">Curated by LNL</div>
                            <h2 className="font-heading text-2xl font-bold text-lnl-charcoal">Campus categories</h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        {CATEGORIES.map((c) => (
                            <div key={c} className="lnl-card p-4 text-center" data-testid={`campus-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                                <div className="font-heading font-semibold text-lnl-charcoal text-sm">{c}</div>
                                <div className="text-[11px] text-lnl-gray mt-1">Photography by administrator</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Visitor contributions gallery — approved submissions only */}
            <section className="py-12 bg-lnl-off-white" data-testid="visitor-contributions">
                <div className="lnl-container">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                        <div>
                            <div className="lnl-eyebrow">Community Stories</div>
                            <h2 className="font-heading text-2xl md:text-3xl font-bold text-lnl-charcoal">Visitor Contributions</h2>
                            <p className="text-sm text-lnl-gray mt-1 max-w-2xl">
                                Real stories and photos shared by our LNL community. Every contribution is reviewed by the LNL administration before publication.
                            </p>
                        </div>
                        <button
                            onClick={() => setShowForm(true)}
                            className="lnl-btn-primary"
                            data-testid="open-share-form"
                        >
                            <Camera className="h-4 w-4" /> Share Your LNL Experience
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-sm text-lnl-gray py-10 text-center">Loading community stories…</div>
                    ) : items.length === 0 ? (
                        <div className="lnl-card p-10 text-center" data-testid="empty-contributions">
                            <Users className="h-8 w-8 mx-auto text-lnl-green" />
                            <div className="font-heading font-semibold text-lnl-charcoal mt-3">Be the first to share your story</div>
                            <p className="text-sm text-lnl-gray mt-1 max-w-md mx-auto">
                                Approved community submissions will appear here. Tap "Share Your LNL Experience" to contribute.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="contribution-grid">
                            {items.map((item) => (
                                <article key={item.id} className="lnl-card overflow-hidden" data-testid={`contribution-${item.id}`}>
                                    {item.image_url ? (
                                        <div className="aspect-[4/3] bg-lnl-off-white overflow-hidden">
                                            <img
                                                src={campusImageUrl(item.id)}
                                                alt={item.caption || `Campus Life submission by ${item.display_name}`}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        </div>
                                    ) : (
                                        <div className="aspect-[4/3] bg-lnl-green-bg grid place-items-center">
                                            <ImagePlus className="h-10 w-10 text-lnl-green" />
                                        </div>
                                    )}
                                    <div className="p-4">
                                        {item.caption && (
                                            <div className="font-heading font-bold text-lnl-charcoal text-sm mb-1">{item.caption}</div>
                                        )}
                                        {item.story && (
                                            <p className="text-sm text-lnl-charcoal/85 leading-relaxed line-clamp-4">{item.story}</p>
                                        )}
                                        <div className="mt-3 pt-3 border-t border-lnl-border flex items-center justify-between text-xs">
                                            <div className="min-w-0">
                                                <div className="font-heading font-semibold text-lnl-charcoal truncate">{item.display_name || "LNL Community"}</div>
                                                {item.affiliation && (
                                                    <div className="text-lnl-gray truncate">{item.affiliation}{item.grad_year ? ` · Class of ${item.grad_year}` : ""}</div>
                                                )}
                                            </div>
                                            {item.created_at && (
                                                <div className="flex items-center gap-1 text-lnl-gray shrink-0">
                                                    <Calendar className="h-3 w-3" />
                                                    {new Date(item.created_at).toLocaleDateString()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {showForm && <ShareStoryModal onClose={() => setShowForm(false)} onSubmitted={load} />}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Share Story Modal
// ---------------------------------------------------------------------------
function ShareStoryModal({ onClose, onSubmitted }) {
    const [form, setForm] = useState({
        name: "", email: "", affiliation: "", caption: "", story: "",
        social_link: "", grad_year: "", notice_ack: false, marketing_consent: false,
    });
    const [imageFile, setImageFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [thanks, setThanks] = useState(false);
    const [honeypot, setHoneypot] = useState(""); // spam trap

    function pickImage(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!/^image\/(jpeg|png|webp)$/.test(f.type)) {
            toast.error("Please choose a JPG, PNG or WEBP image.");
            return;
        }
        if (f.size > 8 * 1024 * 1024) {
            toast.error("Image must be under 8MB.");
            return;
        }
        setImageFile(f);
        setPreview(URL.createObjectURL(f));
    }

    function clearImage() {
        setImageFile(null);
        if (preview) URL.revokeObjectURL(preview);
        setPreview(null);
    }

    async function submit(e) {
        e.preventDefault();
        if (!form.notice_ack) {
            toast.error("Please confirm that you have read and understood the Campus Life Content Notice.");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
            fd.append("website", honeypot); // honeypot
            if (imageFile) fd.append("image", imageFile);
            await submitCampusStory(fd);
            setThanks(true);
            onSubmitted?.();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not submit right now. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed.");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div
                className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="share-story-modal"
            >
                <div className="sticky top-0 bg-white border-b border-lnl-border px-6 py-4 flex items-center justify-between rounded-t-lg z-10">
                    <div>
                        <div className="lnl-eyebrow">Community</div>
                        <h3 className="font-heading text-xl font-bold text-lnl-charcoal">Share Your LNL Experience</h3>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-md hover:bg-lnl-off-white" aria-label="Close" data-testid="close-share-form">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {thanks ? (
                    <div className="p-8 text-center" data-testid="share-success">
                        <div className="w-14 h-14 bg-lnl-green-bg rounded-full grid place-items-center mx-auto">
                            <Send className="h-6 w-6 text-lnl-green" />
                        </div>
                        <h4 className="font-heading font-bold text-lnl-charcoal text-lg mt-4">
                            Thank you for sharing your LNL experience!
                        </h4>
                        <p className="text-sm text-lnl-charcoal/80 mt-2 max-w-md mx-auto leading-relaxed">
                            Your submission has been received and is currently awaiting review by the LNL administration.
                            If approved, your contribution may be published on the LNL Campus Life page.
                        </p>
                        <button onClick={onClose} className="lnl-btn-primary mt-6" data-testid="share-success-close">Close</button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-6 space-y-4" data-testid="share-story-form">
                        {/* Honeypot — hidden from real users */}
                        <input
                            type="text" name="website" value={honeypot}
                            onChange={(e) => setHoneypot(e.target.value)}
                            tabIndex={-1} autoComplete="off"
                            style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
                            aria-hidden="true"
                        />

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-heading font-semibold text-lnl-gray">Your name*</label>
                                <input type="text" required value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2"
                                    data-testid="share-name" />
                            </div>
                            <div>
                                <label className="text-xs font-heading font-semibold text-lnl-gray">Email*</label>
                                <input type="email" required value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2"
                                    data-testid="share-email" />
                                <div className="text-[11px] text-lnl-gray mt-1">Not shown publicly.</div>
                            </div>
                            <div>
                                <label className="text-xs font-heading font-semibold text-lnl-gray">Affiliation with LNL</label>
                                <input type="text" value={form.affiliation}
                                    onChange={(e) => setForm({ ...form, affiliation: e.target.value })}
                                    placeholder="Student, alumnus, parent, staff…"
                                    className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2"
                                    data-testid="share-affiliation" />
                            </div>
                            <div>
                                <label className="text-xs font-heading font-semibold text-lnl-gray">Graduation year</label>
                                <input type="text" value={form.grad_year}
                                    onChange={(e) => setForm({ ...form, grad_year: e.target.value })}
                                    placeholder="e.g. 2018"
                                    className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2"
                                    data-testid="share-gradyear" />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-heading font-semibold text-lnl-gray">Caption</label>
                            <input type="text" value={form.caption} maxLength={120}
                                onChange={(e) => setForm({ ...form, caption: e.target.value })}
                                placeholder="A short caption for your photo/story"
                                className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2"
                                data-testid="share-caption" />
                        </div>

                        <div>
                            <label className="text-xs font-heading font-semibold text-lnl-gray">Your story / message</label>
                            <textarea rows={4} value={form.story}
                                onChange={(e) => setForm({ ...form, story: e.target.value })}
                                placeholder="Tell us about your LNL experience — a memory, a milestone, a message to fellow students."
                                className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2"
                                data-testid="share-story" />
                        </div>

                        <div>
                            <label className="text-xs font-heading font-semibold text-lnl-gray">Photo (optional)</label>
                            {preview ? (
                                <div className="mt-1 relative w-full">
                                    <img src={preview} alt="Preview" className="max-h-64 w-full object-contain rounded-md border border-lnl-border bg-lnl-off-white" />
                                    <button type="button" onClick={clearImage}
                                        className="absolute top-2 right-2 bg-white/90 border border-lnl-border rounded-md px-2 py-1 text-xs font-heading font-semibold"
                                        data-testid="share-clear-image">
                                        Replace / remove
                                    </button>
                                </div>
                            ) : (
                                <label className="mt-1 flex items-center gap-2 cursor-pointer rounded-md border border-dashed border-lnl-border bg-lnl-off-white px-4 py-6 hover:border-lnl-green transition-colors">
                                    <ImagePlus className="h-5 w-5 text-lnl-green" />
                                    <span className="text-sm text-lnl-charcoal">Click to select an image (JPG, PNG, WEBP · max 8MB)</span>
                                    <input type="file" accept="image/jpeg,image/png,image/webp"
                                        onChange={pickImage} className="hidden" data-testid="share-image-input" />
                                </label>
                            )}
                        </div>

                        <div>
                            <label className="text-xs font-heading font-semibold text-lnl-gray">Social media link (optional)</label>
                            <input type="url" value={form.social_link}
                                onChange={(e) => setForm({ ...form, social_link: e.target.value })}
                                placeholder="https://…"
                                className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2"
                                data-testid="share-social" />
                        </div>

                        <section className="rounded-md border border-lnl-gold/40 bg-amber-50/60 p-4" aria-labelledby="cl-notice-title" data-testid="campus-notice">
                            <h3 id="cl-notice-title" className="font-heading font-bold text-sm text-lnl-charcoal">Campus Life Content Notice</h3>
                            <p className="mt-2 text-xs text-lnl-charcoal leading-relaxed">By submitting or sharing content through the Campus Life section, you acknowledge that Lyceum of Northern Luzon may review and, where applicable, use your submitted content for school-related communications, publicity, and marketing, subject to applicable consent, privacy, intellectual property, and other legal requirements.</p>
                            <p className="mt-2 text-xs text-lnl-charcoal leading-relaxed">Please ensure that the information you share is accurate and responsible. Content containing demonstrably false statements, misinformation, defamatory allegations, or other material that unlawfully harms the school or other individuals may be reviewed and addressed in accordance with applicable laws and school policies.</p>
                        </section>

                        <label className="flex items-start gap-2 rounded-md bg-lnl-green-bg/40 border border-lnl-border p-3 cursor-pointer" data-testid="share-consent-label">
                            <input type="checkbox" checked={form.notice_ack}
                                onChange={(e) => setForm({ ...form, notice_ack: e.target.checked })}
                                className="mt-0.5" aria-required="true"
                                data-testid="share-consent" />
                            <span className="text-xs text-lnl-charcoal leading-relaxed">
                                <b>Required:</b> I have read and understood the Campus Life Content Notice.
                            </span>
                        </label>

                        <label className="flex items-start gap-2 rounded-md border border-lnl-border p-3 cursor-pointer" data-testid="share-marketing-label">
                            <input type="checkbox" checked={form.marketing_consent}
                                onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
                                className="mt-0.5" data-testid="share-marketing-consent" />
                            <span className="text-xs text-lnl-charcoal leading-relaxed">
                                <b>Optional:</b> I give Lyceum of Northern Luzon permission to publish my submission (including my name, photo and any people shown, with their permission) on the LNL website and in school publicity and marketing materials. I confirm I have the right to share this content. <span className="text-lnl-gray">Without this consent, your submission can be reviewed but will not be published.</span>
                            </span>
                        </label>

                        <div className="flex items-center justify-end gap-2 pt-2">
                            <button type="button" onClick={onClose} className="lnl-btn-secondary" data-testid="share-cancel">
                                Cancel
                            </button>
                            <button type="submit" disabled={busy} className="lnl-btn-primary" data-testid="share-submit">
                                <Send className="h-4 w-4" /> {busy ? "Submitting…" : "Submit for review"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
